  (() => {
    const bootstrapKey = '__augMobileGalleryLoopV676Bootstrap';
    if (window[bootstrapKey]?.ensure) {
      window[bootstrapKey].ensure();
      return;
    }

    const stateKey = '__augMobileGalleryLoopV676';
    const retryDelays = [0, 50, 150, 300, 600, 1200, 2500];
    let retryIndex = 0;
    let retryTimer;

    const initialize = (gallery) => {
      if (!gallery?.isConnected) return false;

      const existingState = gallery[stateKey];
      if (existingState?.ready) {
        existingState.resume();
        return true;
      }
      if (existingState?.initializing) return false;

      const viewer = Array.from(gallery.children).find(
        (child) => child.id?.startsWith('GalleryViewer-')
      );
      const slider = viewer && Array.from(viewer.children).find(
        (child) => child.id?.startsWith('Slider-Gallery-')
      );
      const previousButton = viewer?.querySelector('button[name="previous"]');
      const nextButton = viewer?.querySelector('button[name="next"]');
      const sliderComponent = window.customElements?.get('slider-component');
      const directSlides = () => Array.from(slider?.children || []).filter(
        (child) => child.nodeType === 1 && child.hasAttribute('data-media-id')
      );

      if (
        !viewer
        || !slider
        || !previousButton
        || !nextButton
        || !sliderComponent
        || !(viewer instanceof sliderComponent)
        || viewer.slider !== slider
        || viewer.prevButton !== previousButton
        || viewer.nextButton !== nextButton
        || directSlides().length < 2
        || typeof gallery.setActiveMedia !== 'function'
      ) {
        return false;
      }

      gallery[stateKey] = { initializing: true };
      const cleanups = [];
      const listen = (target, type, handler, options) => {
        target.addEventListener(type, handler, options);
        cleanups.push(() => target.removeEventListener(type, handler, options));
      };

      try {
        const mobileQuery = window.matchMedia('(max-width: 749px)');
        const controlRow = Array.from(viewer.children).find(
          (child) => child.classList?.contains('slider-buttons')
        );
        let arrowTouch;
        let swipeTouch;
        let enableFrame;
        let cookieFrame;
        let cookieFallbackTimer;
        let cookieElement;
        let cookieObserver;
        let lateCorrection;
        let suppressButtonClick;
        let suppressButtonClickUntil = 0;
        let suppressSliderClickUntil = 0;
        let wrapGeneration = 0;
        let wrapInProgress = false;
        let overrideActive = false;
        let savedScrollBehavior = '';
        let savedScrollBehaviorPriority = '';

        const activeSlides = () => {
          const allSlides = directSlides();
          const visibleSlides = allSlides.filter(
            (slide) => slide.getClientRects().length > 0 && slide.offsetWidth > 0
          );
          return visibleSlides.length ? visibleSlides : allSlides;
        };

        const now = () => (
          window.performance && typeof window.performance.now === 'function'
            ? window.performance.now()
            : Date.now()
        );

        const getTouch = (touches, identifier) => {
          for (let index = 0; index < touches.length; index += 1) {
            if (touches[index].identifier === identifier) return touches[index];
          }
          return null;
        };

        const nearestSlide = (slides = activeSlides()) => {
          if (!slides.length) return null;
          if (!slider.clientWidth) {
            return slides.find((slide) => slide.classList.contains('is-active')) || slides[0];
          }
          const viewportCenter = slider.scrollLeft + slider.clientWidth / 2;
          return slides.reduce((nearest, slide) => {
            const distance = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - viewportCenter);
            return !nearest || distance < nearest.distance ? { slide, distance } : nearest;
          }, null)?.slide || slides[0];
        };

        const edgeTolerance = () => {
          const step = Number(viewer.sliderItemOffset) || slider.clientWidth || 100;
          return Math.max(4, Math.min(24, Math.round(step * 0.08)));
        };

        const edgeAtTouchStart = (slides) => {
          const current = nearestSlide(slides);
          const maximum = Math.max(0, slider.scrollWidth - slider.clientWidth);
          const tolerance = edgeTolerance();
          return {
            first: current === slides[0] && slider.scrollLeft <= tolerance,
            last: current === slides[slides.length - 1] && slider.scrollLeft >= maximum - tolerance,
          };
        };

        const targetForArrow = (name) => {
          const slides = activeSlides();
          if (slides.length < 2) return null;
          const current = nearestSlide(slides);
          if (name === 'next' && current === slides[slides.length - 1]) return slides[0];
          if (name === 'previous' && current === slides[0]) return slides[slides.length - 1];
          return null;
        };

        const enableMobileArrows = () => {
          if (!mobileQuery.matches || activeSlides().length < 2) return;
          previousButton.removeAttribute('disabled');
          nextButton.removeAttribute('disabled');
        };

        const scheduleEnableMobileArrows = () => {
          window.cancelAnimationFrame(enableFrame);
          enableMobileArrows();
          enableFrame = window.requestAnimationFrame(enableMobileArrows);
        };

        const restoreScrollBehavior = () => {
          if (!overrideActive) return;
          if (savedScrollBehavior) {
            slider.style.setProperty(
              'scroll-behavior',
              savedScrollBehavior,
              savedScrollBehaviorPriority
            );
          } else {
            slider.style.removeProperty('scroll-behavior');
          }
          overrideActive = false;
        };

        const suspendSmoothScrolling = () => {
          if (!overrideActive) {
            savedScrollBehavior = slider.style.getPropertyValue('scroll-behavior');
            savedScrollBehaviorPriority = slider.style.getPropertyPriority('scroll-behavior');
            overrideActive = true;
          }
          slider.style.setProperty('scroll-behavior', 'auto', 'important');
        };

        const cancelLateCorrection = () => {
          if (!lateCorrection) return;
          lateCorrection.cancelled = true;
          lateCorrection.timers.forEach((timer) => window.clearTimeout(timer));
          lateCorrection = null;
          restoreScrollBehavior();
        };

        const interruptWrapForNewInput = () => {
          if (!wrapInProgress && !lateCorrection) return;
          wrapGeneration += 1;
          wrapInProgress = false;
          cancelLateCorrection();
        };

        const performWrap = (targetSlide) => {
          if (!mobileQuery.matches || !targetSlide || wrapInProgress) return false;

          cancelLateCorrection();
          wrapInProgress = true;
          const generation = ++wrapGeneration;
          const isFirst = targetSlide === activeSlides()[0];
          const setTargetPosition = () => {
            if (generation !== wrapGeneration || !mobileQuery.matches) return;
            slider.scrollLeft = isFirst ? 0 : Math.max(0, slider.scrollWidth - slider.clientWidth);
          };
          const finish = () => {
            if (generation !== wrapGeneration) return;
            setTargetPosition();
            wrapInProgress = false;
            scheduleEnableMobileArrows();
          };

          const correction = {
            cancelled: false,
            generation,
            targetSlide,
            timers: [],
          };
          const reinforceIfNeeded = () => {
            if (
              correction.cancelled
              || lateCorrection !== correction
              || correction.generation !== wrapGeneration
              || !mobileQuery.matches
              || !correction.targetSlide.isConnected
            ) {
              return;
            }
            const slides = activeSlides();
            if (
              !slides.includes(correction.targetSlide)
              || !correction.targetSlide.classList.contains('is-active')
            ) {
              cancelLateCorrection();
              return;
            }
            const expected = isFirst ? 0 : Math.max(0, slider.scrollWidth - slider.clientWidth);
            if (Math.abs(slider.scrollLeft - expected) > edgeTolerance()) {
              setTargetPosition();
            }
          };
          const completeCorrection = () => {
            if (lateCorrection !== correction) return;
            reinforceIfNeeded();
            if (lateCorrection !== correction) return;
            lateCorrection = null;
            restoreScrollBehavior();
          };

          try {
            suspendSmoothScrolling();
            gallery.setActiveMedia(targetSlide.dataset.mediaId, false);
            setTargetPosition();
            window.setTimeout(setTargetPosition, 0);
            window.requestAnimationFrame(() => {
              setTargetPosition();
              window.requestAnimationFrame(setTargetPosition);
            });
            window.setTimeout(setTargetPosition, 80);
            window.setTimeout(finish, 160);
            lateCorrection = correction;
            correction.timers.push(
              window.setTimeout(reinforceIfNeeded, 320),
              window.setTimeout(completeCorrection, 520)
            );
          } catch (error) {
            cancelLateCorrection();
            restoreScrollBehavior();
            wrapInProgress = false;
            return false;
          }

          scheduleEnableMobileArrows();
          return true;
        };

        const clearCookieLift = () => {
          gallery.style.removeProperty('--aug-cookie-gallery-lift');
          gallery.removeAttribute('data-aug-cookie-gallery-lift');
        };

        const observeCookie = () => {
          const found = document.querySelector('[data-aug-cookie]');
          if (cookieElement && !cookieElement.isConnected) {
            cookieObserver?.disconnect();
            cookieElement = null;
          }
          if (!found || found === cookieElement || typeof MutationObserver !== 'function') return;
          cookieObserver?.disconnect();
          cookieElement = found;
          cookieObserver = new MutationObserver(() => scheduleCookieLift());
          cleanups.push(() => cookieObserver?.disconnect());
          cookieObserver.observe(cookieElement, {
            attributes: true,
            attributeFilter: ['class', 'hidden', 'style'],
          });
          resizeObserver?.observe(cookieElement);
        };

        const updateCookieLift = () => {
          observeCookie();
          if (!mobileQuery.matches || !controlRow || !cookieElement) {
            clearCookieLift();
            return;
          }

          const cookieStyle = window.getComputedStyle(cookieElement);
          const cookieIsUsable = !cookieElement.hidden
            && cookieElement.classList.contains('is-in')
            && cookieStyle.visibility !== 'hidden'
            && cookieStyle.pointerEvents !== 'none'
            && Number.parseFloat(cookieStyle.opacity || '1') > 0;
          if (!cookieIsUsable) {
            clearCookieLift();
            return;
          }

          const currentLift = Number.parseFloat(
            gallery.style.getPropertyValue('--aug-cookie-gallery-lift') || '0'
          ) || 0;
          const rowRect = controlRow.getBoundingClientRect();
          const cookieRect = cookieElement.getBoundingClientRect();
          const sliderRect = slider.getBoundingClientRect();
          const naturalTop = rowRect.top + currentLift;
          const naturalBottom = rowRect.bottom + currentLift;
          const overlap = Math.max(0, naturalBottom - cookieRect.top);
          const maximumLift = Math.max(0, naturalTop - sliderRect.top - 8);
          const lift = Math.min(maximumLift, Math.ceil(overlap > 0 ? overlap + 8 : 0));

          if (lift > 0) {
            gallery.style.setProperty('--aug-cookie-gallery-lift', `${lift}px`);
            gallery.setAttribute('data-aug-cookie-gallery-lift', 'true');
          } else {
            clearCookieLift();
          }
        };

        const scheduleCookieLift = () => {
          window.cancelAnimationFrame(cookieFrame);
          window.clearTimeout(cookieFallbackTimer);
          cookieFrame = window.requestAnimationFrame(updateCookieLift);
          cookieFallbackTimer = window.setTimeout(updateCookieLift, 80);
        };

        const refresh = () => {
          const shouldLoop = mobileQuery.matches && activeSlides().length > 1;
          viewer.enableSliderLooping = shouldLoop;
          if (shouldLoop) scheduleEnableMobileArrows();
          if (!shouldLoop) {
            arrowTouch = null;
            swipeTouch = null;
            wrapGeneration += 1;
            wrapInProgress = false;
            cancelLateCorrection();
            restoreScrollBehavior();
          }
          scheduleCookieLift();
        };

        const resetTransientState = () => {
          cancelLateCorrection();
          arrowTouch = null;
          swipeTouch = null;
          suppressButtonClick = null;
          suppressButtonClickUntil = 0;
          suppressSliderClickUntil = 0;
          wrapGeneration += 1;
          wrapInProgress = false;
          restoreScrollBehavior();
          refresh();
        };

        const onTouchStart = (event) => {
          if (!mobileQuery.matches) return;
          interruptWrapForNewInput();
          if (event.touches.length !== 1) return;
          const touch = event.touches[0];
          const button = event.target.closest?.('button[name="previous"], button[name="next"]');

          suppressButtonClick = null;
          suppressButtonClickUntil = 0;
          suppressSliderClickUntil = 0;
          if (button && viewer.contains(button)) {
            arrowTouch = {
              identifier: touch.identifier,
              button,
              x: touch.clientX,
              y: touch.clientY,
              maxX: 0,
              maxY: 0,
              startedAt: now(),
            };
            return;
          }

          if (!slider.contains(event.target)) return;
          if (event.target.closest?.('a, input, select, textarea, iframe, video, model-viewer')) {
            return;
          }

          const slides = activeSlides();
          if (slides.length < 2) return;
          const edges = edgeAtTouchStart(slides);
          swipeTouch = {
            identifier: touch.identifier,
            x: touch.clientX,
            y: touch.clientY,
            startedAt: now(),
            atFirst: edges.first,
            atLast: edges.last,
            firstSlide: slides[0],
            lastSlide: slides[slides.length - 1],
          };
        };

        const onTouchMove = (event) => {
          if (event.touches.length > 1) {
            arrowTouch = null;
            swipeTouch = null;
            return;
          }
          if (arrowTouch) {
            const touch = getTouch(event.touches, arrowTouch.identifier);
            if (touch) {
              arrowTouch.maxX = Math.max(arrowTouch.maxX, Math.abs(touch.clientX - arrowTouch.x));
              arrowTouch.maxY = Math.max(arrowTouch.maxY, Math.abs(touch.clientY - arrowTouch.y));
            }
          }
        };

        const onTouchEnd = (event) => {
          if (!mobileQuery.matches) {
            arrowTouch = null;
            swipeTouch = null;
            return;
          }

          if (arrowTouch) {
            const state = arrowTouch;
            arrowTouch = null;
            const touch = getTouch(event.changedTouches, state.identifier);
            const isTap = touch
              && now() - state.startedAt <= 800
              && Math.max(state.maxX, Math.abs(touch.clientX - state.x)) <= 12
              && Math.max(state.maxY, Math.abs(touch.clientY - state.y)) <= 12;
            const targetSlide = isTap ? targetForArrow(state.button.name) : null;
            if (targetSlide && performWrap(targetSlide)) {
              event.preventDefault();
              event.stopImmediatePropagation();
              suppressButtonClick = state.button;
              suppressButtonClickUntil = now() + 800;
            } else if (!isTap) {
              suppressButtonClick = state.button;
              suppressButtonClickUntil = now() + 500;
            }
            return;
          }

          if (!swipeTouch) return;
          const state = swipeTouch;
          swipeTouch = null;
          const touch = getTouch(event.changedTouches, state.identifier);
          if (!touch || now() - state.startedAt > 1200) return;
          const deltaX = touch.clientX - state.x;
          const deltaY = touch.clientY - state.y;
          const threshold = Math.max(40, Math.min(72, slider.clientWidth * 0.12));
          const isHorizontal = Math.abs(deltaX) >= threshold
            && Math.abs(deltaX) > Math.abs(deltaY) * 1.25;
          const targetSlide = isHorizontal && state.atFirst && deltaX > 0
            ? state.lastSlide
            : isHorizontal && state.atLast && deltaX < 0
              ? state.firstSlide
              : null;
          if (!targetSlide) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          suppressSliderClickUntil = now() + 900;
          performWrap(targetSlide);
        };

        const onTouchCancel = () => {
          arrowTouch = null;
          swipeTouch = null;
        };

        const onPointerDown = () => {
          interruptWrapForNewInput();
        };

        const onGalleryClick = (event) => {
          const button = event.target.closest?.('button[name="previous"], button[name="next"]');
          const isSuppressedArrowClick = button === suppressButtonClick
            && now() < suppressButtonClickUntil;
          const isSuppressedSwipeClick = slider.contains(event.target)
            && now() < suppressSliderClickUntil;
          if (!isSuppressedArrowClick && !isSuppressedSwipeClick) {
            interruptWrapForNewInput();
          }
        };

        const onClick = (event) => {
          if (!mobileQuery.matches) return;
          if (slider.contains(event.target) && now() < suppressSliderClickUntil) {
            event.preventDefault();
            event.stopImmediatePropagation();
            suppressSliderClickUntil = 0;
            return;
          }

          const button = event.target.closest?.('button[name="previous"], button[name="next"]');
          if (!button || !viewer.contains(button)) return;
          if (button === suppressButtonClick && now() < suppressButtonClickUntil) {
            event.preventDefault();
            event.stopImmediatePropagation();
            suppressButtonClick = null;
            suppressButtonClickUntil = 0;
            return;
          }

          if (wrapInProgress) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
          const targetSlide = targetForArrow(button.name);
          if (!targetSlide || !performWrap(targetSlide)) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        };

        const onSliderScroll = () => {
          scheduleEnableMobileArrows();
          scheduleCookieLift();
        };

        const touchStartOptions = { capture: true, passive: true };
        const touchMoveOptions = { capture: true, passive: true };
        const touchEndOptions = { capture: true, passive: false };
        const touchCancelOptions = { capture: true, passive: true };
        const passiveOptions = { passive: true };
        listen(viewer, 'touchstart', onTouchStart, touchStartOptions);
        listen(viewer, 'touchmove', onTouchMove, touchMoveOptions);
        listen(viewer, 'touchend', onTouchEnd, touchEndOptions);
        listen(viewer, 'touchcancel', onTouchCancel, touchCancelOptions);
        listen(viewer, 'click', onClick, true);
        listen(gallery, 'pointerdown', onPointerDown, true);
        listen(gallery, 'click', onGalleryClick, true);
        listen(slider, 'scroll', onSliderScroll, passiveOptions);

        const arrowObserver = typeof MutationObserver === 'function'
          ? new MutationObserver(enableMobileArrows)
          : null;
        if (arrowObserver) cleanups.push(() => arrowObserver.disconnect());
        arrowObserver?.observe(previousButton, { attributes: true, attributeFilter: ['disabled'] });
        arrowObserver?.observe(nextButton, { attributes: true, attributeFilter: ['disabled'] });

        const resizeObserver = typeof ResizeObserver === 'function'
          ? new ResizeObserver(refresh)
          : null;
        if (resizeObserver) cleanups.push(() => resizeObserver.disconnect());
        resizeObserver?.observe(slider);
        resizeObserver?.observe(viewer);
        if (controlRow) resizeObserver?.observe(controlRow);

        const bodyObserver = typeof MutationObserver === 'function'
          ? new MutationObserver(scheduleCookieLift)
          : null;
        if (bodyObserver) cleanups.push(() => bodyObserver.disconnect());
        if (document.body) {
          bodyObserver?.observe(document.body, {
            attributes: true,
            attributeFilter: ['class'],
            childList: true,
            subtree: true,
          });
        }

        if (typeof mobileQuery.addEventListener === 'function') {
          listen(mobileQuery, 'change', refresh);
        } else {
          mobileQuery.addListener(refresh);
          cleanups.push(() => mobileQuery.removeListener(refresh));
        }
        listen(window, 'resize', refresh, passiveOptions);
        listen(window, 'orientationchange', resetTransientState, passiveOptions);
        listen(window, 'scroll', scheduleCookieLift, passiveOptions);
        if (document.readyState === 'loading') {
          listen(document, 'DOMContentLoaded', scheduleCookieLift, { once: true });
        }
        cleanups.push(() => {
          window.cancelAnimationFrame(enableFrame);
          window.cancelAnimationFrame(cookieFrame);
          window.clearTimeout(cookieFallbackTimer);
          cancelLateCorrection();
          restoreScrollBehavior();
        });

        observeCookie();
        refresh();
        const state = {
          ready: true,
          resume: resetTransientState,
        };
        gallery[stateKey] = state;
        gallery.dataset.augMobileGalleryLoop = 'true';
        return true;
      } catch (error) {
        cleanups.reverse().forEach((cleanup) => {
          try {
            cleanup();
          } catch (cleanupError) {}
        });
        gallery.removeAttribute('data-aug-mobile-gallery-loop');
        delete gallery[stateKey];
        return false;
      }
    };

    const initializeAll = () => {
      let targetCount = 0;
      let allReady = true;
      document.querySelectorAll('media-gallery').forEach((gallery) => {
        const viewer = Array.from(gallery.children).find(
          (child) => child.id?.startsWith('GalleryViewer-')
        );
        const slider = viewer && Array.from(viewer.children).find(
          (child) => child.id?.startsWith('Slider-Gallery-')
        );
        const slideCount = slider
          ? Array.from(slider.children).filter(
              (child) => child.nodeType === 1 && child.hasAttribute('data-media-id')
            ).length
          : 0;
        if (!viewer || !slider || slideCount < 2) return;
        targetCount += 1;
        if (!initialize(gallery)) allReady = false;
      });
      return targetCount > 0 && allReady;
    };

    const ensureInitialized = (resetRetries = false) => {
      if (resetRetries) {
        window.clearTimeout(retryTimer);
        retryIndex = 0;
      }
      if (initializeAll()) {
        window.clearTimeout(retryTimer);
        return;
      }
      if (retryIndex >= retryDelays.length) return;
      const delay = retryDelays[retryIndex];
      retryIndex += 1;
      window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(() => ensureInitialized(false), delay);
    };

    window[bootstrapKey] = { ensure: () => ensureInitialized(true) };
    ensureInitialized(true);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => ensureInitialized(true), { once: true });
    }
    window.addEventListener('pageshow', () => ensureInitialized(true));
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) ensureInitialized(true);
    });
    document.addEventListener('shopify:section:load', () => ensureInitialized(true));

    if (window.customElements?.whenDefined) {
      Promise.all([
        customElements.whenDefined('media-gallery'),
        customElements.whenDefined('slider-component'),
      ]).then(() => ensureInitialized(true));
    }
  })();
